import {
	Component,
	inject,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { ParseDataToValue } from 'app/core/utils/parse-data-value.util.service';
import { Table } from 'primeng/table';
import { LazyLoadEvent } from 'primeng/api';
import { MsToastService } from 'app/shared/services/ms-toast/ms-toast.services';
import { MsStoreService } from 'app/shared/services/ms-store/ms-store.services';

interface MsTableColumnControl {
	view?: MsTableColumnAction;
	edit?: MsTableColumnAction;
	delete?: MsTableColumnAction;
}

interface MsTableColumnAction {
	show?: boolean;
	// eslint-disable-next-line @typescript-eslint/ban-types
	onClick?: Function;
}

interface MsTableControl {
	new?: MsTableAction;
	reset?: MsTableAction;
}

interface MsTableAction {
	show?: boolean;
	onClick?: Function;
}

interface MsTableColumn {
	control?: MsTableColumnControl;
	field?: string;
	header?: string;
	sortable?: boolean;
	searchable?: boolean;
	type?: 'number' | 'string' | 'boolean' | 'control';
	style?: string;
	onChange?: Function;
}

export interface MsTableScope {
	load?: any;
	loading?: boolean;
	data?: any[];
	control?: MsTableControl;
	columns: MsTableColumn[];
	settings: any;
	tableLazyLoadEvent?: LazyLoadEvent;
}

interface Scope {
	table?: MsTableScope;
}

@Component({
	selector: 'ms-table-old',
	templateUrl: './ms-table-old.component.html',
	styleUrls: ['./ms-table-old.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class MsTableOldComponent implements OnInit, OnDestroy {
	formGroup: FormGroup;

	/**
	 * Entrys Parameters
	 */
	@Input({ required: true }) table: MsTableScope;
	@Input({ required: true }) name: string;

	/**
	 * Elements
	 */
	@ViewChild('dt')
	dt: Table;

	/**
	 * Injects
	 */
	_parseDataToValue: ParseDataToValue = inject(ParseDataToValue);
	_formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
	_msToastService: MsToastService = inject(MsToastService);

	scope: Scope = {
		table: {
			loading: false,
			data: [],
			columns: [],
			settings: {
				rowsPerPage: 5,
				rowsPerPageOptions: [5, 10, 15],
				lazy: true,
				paginator: true,
				showCurrentPageReport: true,
				globalFilter: true,
				globalFilterFields: [],
				emptyMessage: 'Nenhum registro encontrado.',
				currentPageReportTemplate: 'Mostrando {first} para {last} de {totalRecords} registros',
				defaultSort: {},
				totalRecord: 0,
			},
		},
	};

	/**
	 * Constructor
	 */
	constructor(private _msStoreService: MsStoreService) {
		this.formGroup = this._formBuilder.group({
			search: [null, []],
		});
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		this._default();
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	clear(): void {
		if (this.scope.table.control.reset?.onClick) {
			this.scope.table.control.reset.onClick();
		}
		this.formGroup.reset();
		this._msStoreService.clear(this.name + '-msTableSearchValue');
		this._msStoreService.clear(this.name + '-multiSortMeta');
		this._msToastService.success({ title: 'Filtro', message: 'Resetado com Sucesso' });
		this.dt.reset();
	}

	onLazyLoad(event: LazyLoadEvent | any) {
		const _tableLazyLoadEventCallBack = this._msStoreService.get(
			this.name + '-tableLazyLoadEventCallBack'
		);
		let _search = null;
		_search = this._getStoreData();
		if (_tableLazyLoadEventCallBack !== null && _tableLazyLoadEventCallBack !== undefined) {
			event = _tableLazyLoadEventCallBack;
			this._msStoreService.clear(this.name + '-tableLazyLoadEventCallBack');
			const _multiSortMeta = this._msStoreService.get(this.name + '-multiSortMeta');
			if (_multiSortMeta != null && _multiSortMeta.length > 0) {
				event['multiSortMeta'] = _multiSortMeta;
				this._msStoreService.clear(this.name + '-multiSortMeta');
			}
		}

		if (!event['globalFilter']) {
			event['globalFilter'] = _search;
		}
		this._msStoreService.set(this.name + '-tableLazyLoadEvent', event);
		this.scope.table.tableLazyLoadEvent = event;
		this.scope.table.loading = true;
		if (event !== undefined) {
			event['fieldsOfGlobalFilter'] = this.scope.table.settings.globalFilterFields;
			this.table.load(event).subscribe({
				next: (data: any) => {
					this.scope.table.settings.totalRecords = data.totalElements;
					this.scope.table.data = data.content;
				},
				error() {},
				complete: (): void => {
					this.scope.table.loading = false;
				},
			});
		}
	}

	onSearch($event): void {
		const _value = $event.target.value || '';
		if (_value.length >= 0) {
			this.dt.filterGlobal(_value, 'contains');
		}
		this.formGroup.get('search').setValue(_value);
		this._msStoreService.set(this.name + '-msTableSearchValue', _value);
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Private methods
	// -----------------------------------------------------------------------------------------------------

	private _default(): void {
		this.scope.table.control = this.table.control;
		this.scope.table.settings = {
			...this.scope.table.settings,
			...this.table.settings,
		};
		this.scope.table.columns = [...this.table.columns, ...this.scope.table.columns];
		if (this.scope.table.control.new) {
			this.scope.table.control.new.show =
				this.scope.table.control.new.show !== undefined
					? this.scope.table.control.new.show
						? this.scope.table.control.new.onClick !== undefined
						: false
					: this.scope.table.control.new.onClick !== undefined;
		}
		this.scope.table.columns.forEach((column: any, index: number) => {
			if (column.sortable === undefined) {
				column.sortable = true;
			}
			if (column.searchable === undefined) {
				column.searchable = true;
			}
			if (column.type === 'control' && column.style === undefined) {
				column.sortable = false;
				column.searchable = false;
				column.style = 'width: 1px';
				if (!column.control.view) {
					column.control.view = { show: false };
				}
				column.control.view.show =
					column.control.view.show !== undefined
						? column.control.view.show
							? column.control.view.onClick !== undefined
							: false
						: column.control.view.onClick !== undefined;
				if (!column.control.edit) {
					column.control.edit = { show: false };
				}
				column.control.edit.show =
					column.control.edit.show !== undefined
						? column.control.edit.show
							? column.control.edit.onClick !== undefined
							: false
						: column.control.edit.onClick !== undefined;
				if (!column.control.delete) {
					column.control.delete = { show: false };
				}
				column.control.delete.show =
					column.control.delete.show !== undefined
						? column.control.delete.show
							? column.control.delete.onClick !== undefined
							: false
						: column.control.delete.onClick !== undefined;

				if (column.control?.new && column.control.new.onClick instanceof Function) {
					const onNew: Function = column.control.new.onClick;
					column.control.new.onClick = (data) => {
						this._setStoreData();
						onNew(data);
					};
				}
				if (column.control?.edit && column.control.edit.onClick instanceof Function) {
					const onEdit: Function = column.control.edit.onClick;
					column.control.edit.onClick = (data) => {
						this._setStoreData();
						onEdit(data);
					};
				}
				if (column.control?.delete && column.control.delete.onClick instanceof Function) {
					const onDelete: Function = column.control.delete.onClick;
					column.control.delete.onClick = (data) => {
						onDelete(data)?.subscribe(() => {
							this.scope.table.tableLazyLoadEvent = this._msStoreService.get(
								this.name + '-tableLazyLoadEvent'
							);
							this.onLazyLoad(this.scope.table.tableLazyLoadEvent);
						});
					};
				}
				if (column.control?.delete && column.control.delete.onClick instanceof Function) {
					const onDelete: Function = column.control.delete.onClick;
					column.control.delete.onClick = (data) => {
						onDelete(data)?.subscribe(() => {
							this.onLazyLoad(this.scope.table.tableLazyLoadEvent);
						});
					};
				}
			}
			return column;
		});
		if (
			this.scope.table.settings.globalFilterFields &&
			this.scope.table.settings.globalFilterFields.lenght > 0
		) {
			this.scope.table.settings.globalFilter = true;
		}
	}

	private _getStoreData(): any {
		const _msTableSearchValue =
			this._msStoreService.get(this.name + '-msTableSearchValue') ||
			this.formGroup.get('search').value;
		this.formGroup.get('search').setValue(_msTableSearchValue);
		return _msTableSearchValue;
	}

	private _setStoreData(): void {
		const _msTableSearchValue =
			this.formGroup.get('search').value ||
			this._msStoreService.get(this.name + '-msTableSearchValue');
		this._msStoreService.set(
			this.name + '-tableLazyLoadEventCallBack',
			this.scope.table.tableLazyLoadEvent
		);
		this._msStoreService.set(this.name + '-msTableSearchValue', _msTableSearchValue);
		this._msStoreService.set(
			this.name + '-multiSortMeta',
			this.scope.table.tableLazyLoadEvent?.multiSortMeta
		);
	}
}
